import { Form, Input, Col, Row, Typography, Select, Flex, Button, Divider, theme } from "antd";
import React, { FC } from "react";
import { PhoneNumberInput } from "@/components/phone-number-input";

const { Title, Text } = Typography;

interface FormVerticalProps {
  formXlsProps: any;
  handleSave?: any;
  formLoading?: boolean;
  closeColapse?: () => void;
}

/**
 * Config de mapeo de columnas del importador de CONTACTOS. Cada campo es el
 * nombre de la columna del XLS que le corresponde (mismo patrón que el form de
 * productos). Clon de `FormVerticalImportsProducts`.
 */
export const FormVerticalImportsContacts: FC<FormVerticalProps> = React.memo(
  ({ formXlsProps, handleSave, closeColapse, formLoading = false }) => {
    const { token } = theme.useToken();

    return (
      <Form
        {...formXlsProps}
        onFinish={handleSave}
        style={{ width: "100%" }}
        layout="vertical"
      >
        <Title
          level={5}
          style={{ color: token.colorTextSecondary, marginBottom: 24 }}
        >
          Indicá el nombre de cada columna del XLS
        </Title>
        <Row gutter={40} wrap justify="start" style={{ width: "100%" }}>
          <Col xs={24} md={12} lg={12}>
            <Form.Item
              style={{ marginBottom: 7 }}
              label="Línea de encabezados"
              name="firstLine"
              rules={[{ required: true }]}
              extra="Número de fila del XLS donde están los títulos de columna (ej. 1)"
            >
              <Input type="number" placeholder="1" />
            </Form.Item>

            <Form.Item
              label="Nombre"
              name="firstname"
              rules={[{ required: true }]}
              extra="Columna del XLS con el nombre del contacto"
            >
              <Input placeholder="Nombre" />
            </Form.Item>

            <Form.Item label="Apellido" name="lastname">
              <Input placeholder="Apellido" />
            </Form.Item>

            <Form.Item
              label="Email"
              name="email"
              extra="Requerido si no hay whatsapp"
            >
              <Input placeholder="Email" />
            </Form.Item>

            <Form.Item
              label="WhatsApp / Teléfono"
              name="whatsapp"
              extra="Requerido si no hay email"
            >
              <PhoneNumberInput />
            </Form.Item>
          </Col>

          <Col xs={24} md={12} lg={12}>
            <Form.Item label="Descripción" name="description">
              <Input placeholder="Descripción" />
            </Form.Item>

            <Form.Item
              label="Grupo"
              name="group"
              extra="Columna con el nombre del grupo (se crea si no existe)"
            >
              <Input placeholder="Grupo" />
            </Form.Item>

            <Form.Item label="Activo" name="isActive">
              <Input placeholder="Activo" />
            </Form.Item>

            <Form.Item label="Formato de fecha" name="dateFormat">
              <Select allowClear>
                <Select.Option value="dd/mm/yyyy">dd/mm/yyyy</Select.Option>
                <Select.Option value="mm/dd/yyyy">mm/dd/yyyy</Select.Option>
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} md={24} lg={24}>
            <Form.Item
              label="Columnas personalizadas"
              name="customColumns"
              extra="Columnas extra del XLS a guardar en additional_data (ej: cargo, sector)"
            >
              <Select
                mode="tags"
                tokenSeparators={[","]}
                allowClear
                placeholder="Escribí los nombres de columna y separá con coma"
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>

          {/* ── Dirección de ENTREGA (opcional) ── */}
          <Col xs={24} md={24} lg={24}>
            <Divider style={{ margin: "8px 0 16px" }} />
            <Title level={5} style={{ color: token.colorTextSecondary, marginBottom: 4 }}>
              Dirección de entrega (opcional)
            </Title>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Si mapeás columnas de entrega, <b>latitud</b> y <b>longitud</b> son
              obligatorias (además de calle y CP). Una dirección incompleta se
              ignora sin frenar la importación del contacto.
            </Text>
          </Col>
          <Col xs={24} md={12} lg={12}>
            <Form.Item label="Calle y número" name={["shippingColumns", "street1"]} style={{ marginTop: 16 }}>
              <Input placeholder="Columna con la calle de entrega" />
            </Form.Item>
            <Form.Item label="Código postal" name={["shippingColumns", "postcode"]}>
              <Input placeholder="CP" />
            </Form.Item>
            <Form.Item label="Latitud" name={["shippingColumns", "lat"]} extra="Obligatoria para entrega">
              <Input placeholder="Latitud" />
            </Form.Item>
            <Form.Item label="Longitud" name={["shippingColumns", "lng"]} extra="Obligatoria para entrega">
              <Input placeholder="Longitud" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12} lg={12}>
            <Form.Item label="Piso / Depto (opcional)" name={["shippingColumns", "street2"]} style={{ marginTop: 16 }}>
              <Input placeholder="Piso, depto, etc." />
            </Form.Item>
            <Form.Item label="Provincia" name={["shippingColumns", "province"]}>
              <Input placeholder="Provincia" />
            </Form.Item>
            <Form.Item label="Ciudad" name={["shippingColumns", "city"]}>
              <Input placeholder="Ciudad" />
            </Form.Item>
            <Form.Item label="Referencia (opcional)" name={["shippingColumns", "reference"]}>
              <Input placeholder="Entre calles, indicaciones" />
            </Form.Item>
          </Col>

          {/* ── Dirección de FACTURACIÓN (opcional) ── */}
          <Col xs={24} md={24} lg={24}>
            <Divider style={{ margin: "8px 0 16px" }} />
            <Title level={5} style={{ color: token.colorTextSecondary, marginBottom: 4 }}>
              Dirección de facturación (opcional)
            </Title>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Si mapeás columnas de facturación, <b>razón social</b>, <b>CUIT</b> y{" "}
              <b>condición IVA</b> son obligatorias (además de calle y CP). La
              condición IVA acepta el nombre ("Responsable Inscripto") o el código.
            </Text>
          </Col>
          <Col xs={24} md={12} lg={12}>
            <Form.Item label="Calle y número" name={["billingColumns", "street1"]} style={{ marginTop: 16 }}>
              <Input placeholder="Columna con la calle de facturación" />
            </Form.Item>
            <Form.Item label="Código postal" name={["billingColumns", "postcode"]}>
              <Input placeholder="CP" />
            </Form.Item>
            <Form.Item label="Razón social" name={["billingColumns", "company"]} extra="Obligatoria para facturación">
              <Input placeholder="Razón social" />
            </Form.Item>
            <Form.Item label="CUIT" name={["billingColumns", "vatId"]} extra="Obligatorio para facturación">
              <Input placeholder="CUIT" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12} lg={12}>
            <Form.Item label="Condición IVA" name={["billingColumns", "billingType"]} extra="Obligatoria para facturación (nombre o código)" style={{ marginTop: 16 }}>
              <Input placeholder="Condición frente al IVA" />
            </Form.Item>
            <Form.Item label="Provincia" name={["billingColumns", "province"]}>
              <Input placeholder="Provincia" />
            </Form.Item>
            <Form.Item label="Ciudad" name={["billingColumns", "city"]}>
              <Input placeholder="Ciudad" />
            </Form.Item>
            <Form.Item label="Piso / Depto (opcional)" name={["billingColumns", "street2"]}>
              <Input placeholder="Piso, depto, etc." />
            </Form.Item>
          </Col>

          <Col xs={24} md={24} lg={24}>
            <Divider style={{ margin: "8px 0 16px" }} />
            <Flex gap={8} justify="end">
              <Button danger onClick={closeColapse}>
                Cancelar
              </Button>
              <Button type="primary" htmlType="submit" loading={formLoading}>
                Guardar
              </Button>
            </Flex>
          </Col>
        </Row>
      </Form>
    );
  },
);
